/**
 * Zero-dependency Client-Side Engagement Analytics Service
 * 
 * Automatically routes analytics events to Mixpanel's REST API if VITE_MIXPANEL_TOKEN 
 * is set. In development, it prints highly polished console logs to facilitate verification.
 */

export interface AnalyticsProperties {
  [key: string]: any;
}

class AnalyticsService {
  private token: string | undefined;
  private isDev: boolean;

  constructor() {
    this.token = import.meta.env.VITE_MIXPANEL_TOKEN;
    this.isDev = import.meta.env.DEV;
  }

  /**
   * Main dispatch function to send events to Mixpanel and/or log in development
   */
  public track(eventName: string, properties: AnalyticsProperties = {}): void {
    // 1. Log to console in development with premium aesthetics
    if (this.isDev) {
      console.log(
        `%c📊 [Analytics] %cEvent: %c${eventName}%c Properties:`,
        "color: #a855f7; font-weight: bold; font-size: 11px;",
        "color: #71717a; font-weight: normal;",
        "color: #10b981; font-weight: bold; background: rgba(16, 185, 129, 0.1); padding: 2px 6px; border-radius: 4px;",
        "color: #71717a; font-weight: normal;",
        properties
      );
    }

    // 2. Dispatch to Mixpanel REST API if a token is configured
    if (this.token) {
      const distinctId = properties.distinct_id || "anonymous_user";
      
      const payload = {
        event: eventName,
        properties: {
          token: this.token,
          distinct_id: distinctId,
          time: Math.floor(Date.now() / 1000),
          $lib_version: "opoko-custom-1.0",
          ...properties,
        },
      };

      const params = new URLSearchParams();
      params.append("data", JSON.stringify(payload));

      fetch("https://api.mixpanel.com/track#live-event", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params.toString(),
        mode: "cors",
      }).catch((err) => {
        console.warn("[Analytics] Network error dispatching event to Mixpanel:", err);
      });
    }
  }

  /**
   * Track when the app or session is first opened
   */
  public trackAppOpen(userId?: string, email?: string): void {
    const props: AnalyticsProperties = {};
    if (userId) {
      props.distinct_id = userId;
      props.userId = userId;
    }
    if (email) {
      props.userEmail = email;
    }
    this.track("session_start", props);
  }

  /**
   * Track when a vote is successfully cast on a poll
   */
  public trackVoteSubmitted(
    pollId: string,
    category: string,
    optionId: string,
    userId: string
  ): void {
    this.track("vote_submitted", {
      distinct_id: userId,
      userId,
      pollId,
      category,
      optionId,
    });
  }

  /**
   * Track when a user skips a poll in their feed
   */
  public trackPollSkipped(pollId: string, category: string, userId: string): void {
    this.track("poll_skipped", {
      distinct_id: userId,
      userId,
      pollId,
      category,
    });
  }

  /**
   * Track when a user successfully creates a new poll
   */
  public trackPollCreated(pollId: string, category: string, userId: string): void {
    this.track("poll_created", {
      distinct_id: userId,
      userId,
      pollId,
      category,
    });
  }
}

export const analyticsService = new AnalyticsService();
